import { useEffect, useState } from "react";
import { ArticleElementsFactory } from "@routes/Article/components/ArticleParagraphElements/storyElement/ArticleElementsFactory";
import { getElementType } from "@routes/Article/components/ArticleParagraphElements/utils/helpers";
import { ArticleStoryElementProps } from "@routes/Article/components/ArticleParagraphElements/utils/types";
import DOMPurify from "dompurify";
import parse, { domToReact } from "html-react-parser";

/**
 * Renders a story element in an article.
 * This component processes the body string to identify and render different types of story elements
 * such as paragraphs, images, podcasts, etc., based on their HTML structure.
 */
const StoryElement = ({
  bodyString,
  className,
  article,
  gsChannels,
  adsWrapperClass,
  displayReadMoreAndNewsletterSignupForm,
  index,
  articleIndex,
  isBoxWrapped,
  paragraphIndices,
  paragraphWidth,
  isFullContent,
}: ArticleStoryElementProps): React.ReactElement => {
  const [sanitizeString, setSanitizeString] = useState<string>(bodyString);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const htmlString = DOMPurify.sanitize(bodyString, {
      ADD_TAGS: ["iframe"],
      ADD_ATTR: [
        "allow",
        "allowfullscreen",
        "frameborder",
        "scrolling",
        "src",
        "width",
        "height",
        "style",
        "id",
        "class",
        "className",
        "title",
        "referrerpolicy",
      ],
    });

    setSanitizeString(htmlString);
  }, [bodyString]);

  return (
    <>
      {parse(sanitizeString, {
        replace(node) {
          const elementType = getElementType(node);

          if (!elementType) {
            return domToReact([node]);
          }

          const ElementFactory = ArticleElementsFactory(elementType); // Default layout

          if (!ElementFactory) {
            return domToReact([node]);
          }

          return (
            <>
              <ElementFactory
                bodyString={bodyString}
                className={className}
                article={article}
                gsChannels={gsChannels}
                adsWrapperClass={adsWrapperClass}
                displayReadMoreAndNewsletterSignupForm={
                  displayReadMoreAndNewsletterSignupForm
                }
                index={index}
                articleIndex={articleIndex}
                isBoxWrapped={isBoxWrapped}
                paragraphIndices={paragraphIndices}
                paragraphWidth={paragraphWidth}
                isFullContent={isFullContent}
              />
            </>
          );
        },
      })}
    </>
  );
};

export default StoryElement;
